<template>
  <div class="criterion-group-card">
    <div class="criterion-group-header">
      <div class="group-match-badge" :class="{ 'match-any': groupOperator === 'OR' }">
        <i :class="groupOperator === 'AND' ? 'fa fa-check-double' : 'fa fa-list'" class="me-2"></i>
        {{ groupOperator === 'AND' ? 'Match ALL' : 'Match ANY' }}
      </div>
      <div class="header-actions">
        <select
          class="form-select form-select-sm operator-select"
          v-model="localOperator"
          :disabled="isViewOnly"
          @change="handleOperatorChange"
        >
          <option value="AND">AND (Match All)</option>
          <option value="OR">OR (Match Any)</option>
        </select>
        <button
          v-if="!isViewOnly"
          class="btn btn-sm btn-outline-danger ms-2"
          @click="$emit('deleteGroup')"
          title="Delete group"
        >
          <i class="fa fa-trash"></i>
        </button>
      </div>
    </div>

    <div class="criterion-group-body">
      <div
        v-for="(item, index) in allItems"
        :key="`item-${index}`"
      >
        <!-- Leaf Criterion -->
        <Criterion
          v-if="item.type === 'criterion'"
          :criterion="item.data"
          :columns="columns"
          :column-values="columnValues"
          :loading-values="getLoadingValue(index)"
          :is-view-only="isViewOnly"
          @update:criterion="handleCriterionUpdate(item.index, $event)"
          @columnSelected="$emit('columnSelected', { column: $event, groupIndex, criterionIndex: item.index })"
          @delete="handleDeleteCriterion(item.index)"
        />

        <!-- Nested Group -->
        <div
          v-else-if="item.type === 'group'"
          class="nested-group-container"
        >
          <CriterionGroup
            :group-index="`${groupIndex}-${item.index}`"
            :criteria="item.data.criteria || []"
            :group-operator="item.data.operator || 'AND'"
            :groups="item.data.groups || []"
            :columns="columns"
            :column-values="columnValues"
            :loading-values="getNestedGroupLoadingValues(item.index)"
            :is-view-only="isViewOnly"
            @update:criteria="handleNestedGroupCriteriaUpdate(item.index, $event)"
            @update:groupOperator="handleNestedGroupOperatorUpdate(item.index, $event)"
            @update:groups="handleNestedGroupsUpdate(item.index, $event)"
            @columnSelected="handleNestedColumnSelected(item.index, $event)"
            @deleteGroup="handleDeleteNestedGroup(item.index)"
          />
        </div>

        <!-- Operator divider between items -->
        <div v-if="index < allItems.length - 1" class="criterion-operator-divider">
          <span class="operator-badge small-badge">
            {{ localOperator }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="!isViewOnly" class="criterion-group-footer">
      <button
        class="btn btn-sm btn-outline-primary me-2"
        @click="addCriterion"
        :disabled="!canAddCriterion"
      >
        <i class="fa fa-plus me-2"></i>
        Add Criterion
      </button>
      <button
        class="btn btn-sm btn-outline-secondary"
        @click="addNestedGroup"
      >
        <i class="fa fa-layer-group me-2"></i>
        Add Nested Group
      </button>
    </div>
  </div>
</template>

<script>
import Criterion from './Criterion.vue'

export default {
  name: 'CriterionGroup',
  components: {
    Criterion,
  },
  props: {
    groupIndex: {
      type: [Number, String],
      required: true
    },
    criteria: {
      type: Array,
      default: () => [{
        column: null,
        condition: 'IN',
        values: [],
        lowerbound: null,
        upperbound: null,
        singleValue: null,
      }]
    },
    groupOperator: {
      type: String,
      default: 'AND'
    },
    groups: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      default: () => []
    },
    columnValues: {
      type: Object,
      default: () => ({})
    },
    loadingValues: {
      type: Object,
      default: () => ({})
    },
    isViewOnly: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      localCriteria: [],
      localGroups: [],
      localOperator: 'AND',
    }
  },
  emits: ['update:criteria', 'update:groupOperator', 'update:groups', 'columnSelected', 'deleteGroup'],
  mounted() {
    this.localCriteria = JSON.parse(JSON.stringify(this.criteria || []))
    this.localGroups = JSON.parse(JSON.stringify(this.groups || []))
    this.localOperator = this.groupOperator
  },
  computed: {
    canAddCriterion() {
      // Can add if last criterion has column and some value
      if (this.localCriteria.length === 0) return true
      const last = this.localCriteria[this.localCriteria.length - 1]
      if (!last.column) return false
      if (last.condition === 'IN' && last.values.length === 0) return false
      if (last.condition === 'BETWEEN' && (!last.lowerbound || !last.upperbound)) return false
      if (last.condition === 'EQUAL' && !last.singleValue) return false
      return true
    },
    allItems() {
      // Combine criteria and nested groups into a single array for rendering
      const items = []
      
      // Add criteria
      this.localCriteria.forEach((criterion, index) => {
        items.push({
          type: 'criterion',
          index,
          data: criterion
        })
      })
      
      // Add nested groups
      this.localGroups.forEach((group, index) => {
        items.push({
          type: 'group',
          index,
          data: group
        })
      })
      
      return items
    },
  },
  methods: {
    getLoadingValue(index) {
      // Find the actual criterion index in the items array
      const item = this.allItems[index]
      if (item && item.type === 'criterion') {
        return this.loadingValues[item.index] || false
      }
      return false
    },
    getNestedGroupLoadingValues(groupIndex) {
      // Return loading values for nested group (can be enhanced if needed)
      return {}
    },
    handleCriterionUpdate(index, criterion) {
      // Create a new array reference to ensure reactivity
      this.localCriteria = [
        ...this.localCriteria.slice(0, index),
        criterion,
        ...this.localCriteria.slice(index + 1)
      ]
      this.emitUpdate()
    },
    handleDeleteCriterion(index) {
      // Only allow deletion if there's at least one item remaining (criterion or group)
      if (this.localCriteria.length + this.localGroups.length > 1) {
        this.localCriteria.splice(index, 1)
        this.emitUpdate()
      }
    },
    addCriterion() {
      this.localCriteria.push({
        column: null,
        condition: 'IN',
        values: [],
        lowerbound: null,
        upperbound: null,
        singleValue: null,
      })
      this.emitUpdate()
    },
    addNestedGroup() {
      this.localGroups.push({
        operator: 'AND',
        criteria: [{
          column: null,
          condition: 'IN',
          values: [],
          lowerbound: null,
          upperbound: null,
          singleValue: null,
        }],
        groups: []
      })
      this.emitUpdate()
    },
    handleNestedGroupCriteriaUpdate(groupIndex, criteria) {
      // Create a new object reference to ensure reactivity
      this.localGroups[groupIndex] = {
        ...this.localGroups[groupIndex],
        criteria: criteria
      }
      // Force new array reference
      this.localGroups = [...this.localGroups]
      this.emitUpdate()
    },
    handleNestedGroupOperatorUpdate(groupIndex, operator) {
      // Create a new object reference to ensure reactivity
      this.localGroups[groupIndex] = {
        ...this.localGroups[groupIndex],
        operator: operator
      }
      // Force new array reference
      this.localGroups = [...this.localGroups]
      this.emitUpdate()
    },
    handleNestedGroupsUpdate(groupIndex, groups) {
      // Create a new object reference to ensure reactivity
      this.localGroups[groupIndex] = {
        ...this.localGroups[groupIndex],
        groups: groups
      }
      // Force new array reference
      this.localGroups = [...this.localGroups]
      this.emitUpdate()
    },
    handleNestedColumnSelected(groupIndex, event) {
      // Forward the column selected event with proper path
      this.$emit('columnSelected', {
        ...event,
        groupIndex: `${this.groupIndex}-${groupIndex}`,
      })
    },
    handleDeleteNestedGroup(groupIndex) {
      // Only allow deletion if there's at least one item remaining
      if (this.localCriteria.length + this.localGroups.length > 1) {
        this.localGroups.splice(groupIndex, 1)
        this.emitUpdate()
      }
    },
    handleOperatorChange() {
      this.$emit('update:groupOperator', this.localOperator)
    },
    emitUpdate() {
      this.$emit('update:criteria', JSON.parse(JSON.stringify(this.localCriteria)))
      this.$emit('update:groups', JSON.parse(JSON.stringify(this.localGroups)))
    },
  },
  watch: {
    criteria: {
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.localCriteria)) {
          this.localCriteria = JSON.parse(JSON.stringify(newVal || []))
        }
      },
      deep: true
    },
    groups: {
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.localGroups)) {
          this.localGroups = JSON.parse(JSON.stringify(newVal || []))
        }
      },
      deep: true
    },
    groupOperator(newVal) {
      this.localOperator = newVal
    },
  },
}
</script>

<style scoped>
.criterion-group-card {
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.criterion-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-bottom: 2px solid #dee2e6;
}

.group-match-badge {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--bs-primary, #2C7BE5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.group-match-badge.match-any {
  color: var(--bs-warning, #f6c343);
}

.header-actions {
  display: flex;
  align-items: center;
}

.operator-select {
  width: auto;
  min-width: 150px;
}

.criterion-group-body {
  padding: 1.5rem;
  background-color: #fafbfc;
}

.criterion-operator-divider {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5rem 0;
  position: relative;
}

.criterion-operator-divider::before {
  content: '';
  position: absolute;
  left: 20%;
  right: 20%;
  top: 50%;
  height: 1px;
  background: linear-gradient(to right, transparent, #dee2e6 20%, #dee2e6 80%, transparent);
}

.operator-badge {
  background: linear-gradient(135deg, var(--bs-primary, #2C7BE5), #0056b3);
  color: white;
  padding: 0.25rem 1rem;
  border-radius: 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 1;
}

.operator-badge.small-badge {
  padding: 0.2rem 0.75rem;
  font-size: 0.7rem;
}

.criterion-group-footer {
  display: flex;
  justify-content: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.criterion-group-footer .btn {
  font-weight: 500;
  padding: 0.5rem 1.25rem;
  transition: all 0.2s ease;
}

.criterion-group-footer .btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.2);
}

.nested-group-container {
  margin: 1rem 0;
  padding-left: 1rem;
  border-left: 3px solid var(--bs-primary, #2C7BE5);
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1rem;
}
</style>
